'use client';

import React, { useState } from 'react';
import ScrollReveal from './ScrollReveal';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Input } from './Input';
import { useTrackButtonClick } from '@/hooks/useTrackButtonClick';



const Contact = () => {
    const [formData, setFormData] = useState({ name: '', email: '', message: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [status, setStatus] = useState('');
    const trackClick = useTrackButtonClick();
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { id, value } = e.target;
        setFormData((prev) => ({ ...prev, [id]: value }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const res = await fetch('/api/send', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to send email');

            setStatus('Message sent successfully!');

            toast.success("Message sent successfully!");

            setFormData({ name: '', email: '', message: '' }); // clear form
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Unknown error';
            alert('Error: ' + message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="bg-[#1A1A23]/90 text-center rounded-2xl">
            <div className="sm:px-16 px-4 sm:py-16 py-4 w-full h-full mx-auto relative">
                <ScrollReveal>
                    <h2 className="sectionTitle reveal-item">Get in Touch</h2>
                    <p className="text-xl text-gray-400 mb-8 reveal-item">
                        If you have questions, feedback or partnership inquiries, we&apos;d love to hear from you.
                    </p>

                    <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
                        <div className="flex flex-col md:flex-row gap-6 reveal-item">
                            <div className="flex flex-col flex-1">
                                <label htmlFor="name" className="mb-2 text-base text-yellow-500 text-left">
                                    Name
                                </label>
                                <Input
                                    type="text"
                                    id="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Your Name"
                                    required
                                />
                            </div>

                            <div className="flex flex-col flex-1">
                                <label htmlFor="email" className="mb-2 text-base text-yellow-500 text-left">
                                    Email
                                </label>
                                <Input
                                    type="email"
                                    id="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="Your Email"
                                    required
                                />
                            </div>
                        </div>

                        <div className="flex flex-col reveal-item">
                            <label htmlFor="message" className="mb-2 text-base text-yellow-500 text-left">
                                Message
                            </label>
                            <Input
                                as="textarea"
                                id="message"
                                value={formData.message}
                                onChange={handleChange}
                                rows={4}
                                placeholder="Your Message"
                                required
                            />
                        </div>

                        <div className="mt-8 flex reveal-item">
                            <button
                                type="submit"
                                aria-label='Send Message'
                                title='Click to Send Message'
                                disabled={isSubmitting}
                                onClick={() => trackClick("Send Message Button Home Page")}
                                className="inline-flex items-center justify-center
                                    rounded-md font-semibold px-6 py-3 transition duration-200
                                    disabled:opacity-50 disabled:cursor-not-allowed bg-[#F0C869] 
                                    text-black hover:bg-yellow-400 active:bg-yellow-600 mb-4 
                                    shadow-[0_4px_10px_0_rgba(240,200,105,0.5)] 
                                    hover:shadow-[0_6px_15px_0_rgba(240,200,105,0.7)]"
                            >
                                {isSubmitting ? "Sending..." : "Send Message"}
                            </button>
                        </div>

                        {status && <p className="mt-4 text-lg text-yellow-500">{status}</p>}
                    </form>
                </ScrollReveal>
            </div>
        </section>
    );
};

export default Contact;
